import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography, Card, CardContent, Button, Alert, Box, Chip, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButtonGroup, ToggleButton, TextField, CircularProgress,
} from '@mui/material';
import { useProject } from '../../hooks/ProjectContext';

// Geometry review — stage ⑤ of the review flow (docs/geometry-review-flow-design.md).
// Upload a KML that was downloaded with "for editing" and edited in Google Earth;
// the server diffs it against that export's own manifest. HARD changes need an
// explicit per-row decision (no bulk approve, per design v4 §8); detected-but-not-
// appliable changes are shown so nothing is silently dropped.

function bearerHeaders(json) {
  const tkn = localStorage.getItem('tkn');
  return { ...(json ? { 'Content-Type': 'application/json' } : {}), ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const changeKey = (c) => `${c.changeType}:${c.id == null ? (c.after && c.after.name) : c.id}`;

// Small before/after overlay for a boundary reshape — grey = current platform
// shape, green dashed = proposed. Enough to see WHAT changed before approving
// (vertex counts alone can hide completely different shapes).
function ReshapePreview({ before, after }) {
  const b = before && before.coordinates && before.coordinates[0];
  const a = after && after.coordinates && after.coordinates[0];
  if (!b || !a) return null;
  const all = b.concat(a);
  const minX = Math.min(...all.map((c) => c[0])); const maxX = Math.max(...all.map((c) => c[0]));
  const minY = Math.min(...all.map((c) => c[1])); const maxY = Math.max(...all.map((c) => c[1]));
  const W = 180; const H = 120; const pad = 8;
  const sx = (x) => pad + ((x - minX) / ((maxX - minX) || 1)) * (W - 2 * pad);
  const sy = (y) => H - pad - ((y - minY) / ((maxY - minY) || 1)) * (H - 2 * pad);
  const pts = (ring) => ring.map((c) => `${sx(c[0]).toFixed(1)},${sy(c[1]).toFixed(1)}`).join(' ');
  return (
    <Box sx={{ flexShrink: 0, textAlign: 'center' }}>
      <svg width={W} height={H} style={{ border: '1px solid #ddd', borderRadius: 4, display: 'block' }}>
        <polygon points={pts(b)} fill="rgba(120,120,120,0.15)" stroke="#888" strokeWidth="1.5" />
        <polygon points={pts(a)} fill="rgba(46,125,50,0.12)" stroke="#2e7d32" strokeWidth="1.5" strokeDasharray="4 3" />
      </svg>
      <Typography variant="caption" color="text.secondary">grey = current · dashed = proposed</Typography>
    </Box>
  );
}

function ChangeSummary({ c }) {
  if (c.changeType === 'evidence-removed') return <span>Evidence removed from the file — approving RETRACTS it (kept in history, never deleted)</span>;
  if (c.changeType === 'boundary-reshaped') return <span>Boundary <strong>{c.before?.name}</strong> reshaped ({c.before?.vertices}→{c.after?.vertices} vertices) — applied with a pre-change snapshot</span>;
  if (c.changeType === 'renamed') return <span><strong>{c.before?.name}</strong> → <strong>{c.after?.name}</strong></span>;
  if (c.changeType === 'point-moved') return <span>Planned point <strong>{c.before?.name}</strong> moved {c.after?.movedMetres} m — canonical geometry, needs a new artifact version</span>;
  if (c.changeType === 'feature-renamed') return <span>Planned feature <strong>{c.before?.name}</strong> → <strong>{c.after?.name}</strong> — names are data keys, needs a new artifact version</span>;
  if (c.changeType === 'feature-type-changed') return <span>Planned feature <strong>{c.before?.name}</strong> changed type {c.before?.type} → {c.after?.type} — unsupported</span>;
  if (c.changeType === 'evidence-moved') return <span>Evidence pin moved {c.after?.movedMetres} m — approving RELOCATES it as a manual GPS correction; the original fix stays stamped in the record</span>;
  if (c.changeType === 'placemark-added' && c.dest === 'boundary') return <span>New polygon <strong>{c.after?.name}</strong> (drawn in {c.after?.folder || 'Boundary'}) — approving adds it to the <strong>{c.after?.siteHint ? `${c.after.siteHint} ` : ''}site boundary</strong>, shown automatically</span>;
  if (c.changeType === 'placemark-added') return <span>New {c.subjectType} <strong>{c.after?.name}</strong>{c.after?.folder ? ` (in ${c.after.folder})` : ''} — approving adds it to the Library as an "External edits" layer</span>;
  if (c.changeType === 'feature-removed') return <span>Planned feature <strong>{c.before?.name}</strong> removed — needs a new artifact version</span>;
  if (c.changeType === 'feature-reshaped') return <span>Planned polygon <strong>{c.before?.name}</strong> reshaped — needs a new artifact version</span>;
  if (c.changeType === 'boundary-removed') return <span>Boundary <strong>{c.before?.name}</strong> missing from the file — removal is rarely intended</span>;
  return <span>{c.changeType}</span>;
}

export default function GeometryReviewTab() {
  const { selectedProject, selectedSite } = useProject();
  const [reviews, setReviews] = useState([]);
  const [review, setReview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [busy, setBusy] = useState(false);
  const [loadingReview, setLoadingReview] = useState(false);
  const [error, setError] = useState('');
  const [applyResult, setApplyResult] = useState(null);
  // Monotonic request id: a decision may only ever attach to the review whose
  // data is CURRENT — stale fetch responses are discarded, and rows are disabled
  // while a load is in flight (an approval clicked mid-load once landed on the
  // next review's same-keyed change).
  const reqSeq = useRef(0);

  const loadList = useCallback(() => {
    if (!selectedProject?.code) { setReviews([]); return; }
    fetch(`/v1/geometry/reviews?project=${encodeURIComponent(selectedProject.code)}`, { headers: bearerHeaders() })
      .then((r) => r.json()).then((d) => setReviews(d.reviews || [])).catch(() => setReviews([]));
  }, [selectedProject?.code]);

  useEffect(loadList, [loadList]);

  const openReview = (id) => {
    const seq = ++reqSeq.current;
    setError(''); setApplyResult(null); setDecisions({}); setLoadingReview(true);
    fetch(`/v1/geometry/reviews/${id}`, { headers: bearerHeaders() })
      .then((r) => r.json())
      .then((d) => {
        if (seq !== reqSeq.current) return; // a newer load superseded this one
        if (d.ok) { setReview(d.review); setDecisions({}); setApplyResult(d.review.result); }
        else setError(d.message || 'load failed');
        setLoadingReview(false);
      })
      .catch((e) => { if (seq === reqSeq.current) { setError(String(e)); setLoadingReview(false); } });
  };

  const onUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setError(''); setReview(null); setApplyResult(null); setDecisions({});
    try {
      const kml = await file.text();
      const res = await fetch('/v1/geometry/reviews', {
        method: 'POST', headers: bearerHeaders(true),
        body: JSON.stringify({ kml, filename: file.name }),
      });
      if (res.status === 401) { setError('Session expired — reload the page and sign in again, then re-upload the same file.'); return; }
      const d = await res.json();
      if (!d.ok) { setError(d.message || d.error || `upload failed (HTTP ${res.status})`); return; }
      loadList();
      openReview(d.id);
    } catch (err) { setError(String(err?.message || err)); } finally { setBusy(false); }
  };

  const decide = (key, decision) => setDecisions((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), decision } }));
  const setReason = (key, reason) => setDecisions((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), reason } }));

  const appliableKeys = review ? (review.appliable || []) : [];
  const undecided = appliableKeys.filter((k) => !decisions[k] || !['approve', 'reject'].includes(decisions[k].decision));
  // An approved retraction needs a written reason before Apply unlocks — the
  // server enforces the same rule.
  const missingReason = review
    ? review.changes.filter((c) => (c.changeType === 'evidence-removed' || c.changeType === 'evidence-moved') && appliableKeys.includes(changeKey(c)))
        .map(changeKey)
        .filter((k) => decisions[k] && decisions[k].decision === 'approve' && !(decisions[k].reason || '').trim())
    : [];
  const alreadyApplied = review && review.status === 'applied';
  const rowsLocked = busy || loadingReview;

  const onApply = async () => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`/v1/geometry/reviews/${review.id}/apply`, {
        method: 'POST', headers: bearerHeaders(true), body: JSON.stringify({ decisions }),
      });
      const d = await res.json();
      if (!d.ok) { setError(d.message || 'apply failed'); return; }
      setApplyResult(d);
      loadList();
      openReview(review.id);
    } catch (err) { setError(String(err?.message || err)); } finally { setBusy(false); }
  };

  const changes = review ? review.changes : [];
  const hardAppliable = changes.filter((c) => appliableKeys.includes(changeKey(c)));
  const informational = changes.filter((c) => c.hard && !appliableKeys.includes(changeKey(c)));
  const soft = changes.filter((c) => !c.hard && !appliableKeys.includes(changeKey(c)));

  return (
    <>
      <Box>
        <Typography variant="h6" gutterBottom>Geometry review</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Round-trip: <strong>Download for editing</strong> below → edit in Google Earth → export as KML → upload here.
          The folder hierarchy is the data structure — a polygon drawn under <strong>Boundary / &lt;site&gt;</strong> becomes
          part of that site's boundary. Changes are applied per row; nothing is ever deleted, only retracted.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button variant="outlined" size="small" disabled={!selectedProject?.code}
                href={selectedProject?.code
                  ? `/v1/export/kml/project?project=${encodeURIComponent(selectedProject.code)}${selectedSite?.code ? `&site=${encodeURIComponent(selectedSite.code)}` : ''}&for=editing`
                  : undefined}>
                Download for editing{selectedSite?.code ? ` (${selectedSite.code})` : selectedProject?.code ? ` (${selectedProject.code})` : ''}
              </Button>
              <Button variant="contained" component="label" size="small" disabled={busy}>
                {busy ? <CircularProgress size={18} /> : 'Upload edited KML'}
                <input type="file" accept=".kml" hidden onChange={onUpload} />
              </Button>
              <Typography variant="caption" color="text.secondary">
                Edit the downloaded file in Google Earth, then upload it here. It is stored verbatim and diffed against its own export manifest.
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        {reviews.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Reviews</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>#</TableCell><TableCell>File</TableCell><TableCell>By</TableCell>
                    <TableCell>Changes</TableCell><TableCell>Errors</TableCell><TableCell>Status</TableCell><TableCell>When</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {reviews.map((r) => (
                      <TableRow key={r.id} hover selected={review?.id === r.id} sx={{ cursor: 'pointer' }} onClick={() => openReview(r.id)}>
                        <TableCell>{r.id}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{r.filename || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{r.actor}</TableCell>
                        <TableCell>{r.changeCount}</TableCell>
                        <TableCell>{r.errorCount ? <Chip label={r.errorCount} color="error" size="small" sx={{ height: 18 }} /> : 0}</TableCell>
                        <TableCell><Chip label={r.status} size="small" color={r.status === 'applied' ? 'success' : 'default'} sx={{ height: 18, fontSize: 10 }} /></TableCell>
                        <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(r.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {review && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Review #{review.id} — {review.filename || 'uploaded file'}
                {review.site ? ` · ${review.site}` : ''} (manifest {review.manifestId})
              </Typography>

              {review.errors && review.errors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <strong>Blocking — re-export and re-edit:</strong>
                  <ul style={{ margin: '4px 0 0 16px' }}>{review.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </Alert>
              )}

              {changes.length === 0 && !review.errors?.length && (
                <Alert severity="success">No changes detected — the file matches its export.</Alert>
              )}

              {hardAppliable.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>Needs your decision</Typography>
                  {hardAppliable.map((c) => {
                    const key = changeKey(c);
                    const d = decisions[key] || {};
                    const recorded = alreadyApplied && review.decisions ? review.decisions[key] : null;
                    return (
                      <Box key={key} sx={{ border: '1px solid', borderColor: d.decision || alreadyApplied ? 'divider' : 'warning.main', borderRadius: 1, p: 1.5, my: 1, opacity: loadingReview ? 0.5 : 1 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          {c.subjectType === 'evidence' && c.id && (
                            <img src={`/v1/evidence/${c.id}/thumb`} alt="" width={72} height={72}
                              style={{ objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                              onError={(ev) => { ev.target.style.display = 'none'; }} />
                          )}
                          {c.changeType === 'boundary-reshaped' && (
                            <ReshapePreview before={c.before && c.before.geojson} after={c.after && c.after.geojson} />
                          )}
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2"><ChangeSummary c={c} /></Typography>
                            <Typography variant="caption" color="text.secondary">{c.recommend}</Typography>
                            {recorded && (
                              <Box sx={{ mt: 1 }}>
                                <Chip size="small" label={recorded.decision} color={recorded.decision === 'approve' ? 'success' : 'default'} sx={{ height: 20, mr: 1 }} />
                                {recorded.reason && <Typography variant="caption" color="text.secondary">“{recorded.reason}”</Typography>}
                              </Box>
                            )}
                            {!alreadyApplied && (
                              <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                                <ToggleButtonGroup exclusive size="small" value={d.decision || null} disabled={rowsLocked}
                                  onChange={(_, v) => v && decide(key, v)}>
                                  <ToggleButton value="approve" color="success">Approve</ToggleButton>
                                  <ToggleButton value="reject" color="error">Reject</ToggleButton>
                                </ToggleButtonGroup>
                                {d.decision === 'approve' && (c.changeType === 'evidence-removed' || c.changeType === 'evidence-moved') && (
                                  <TextField size="small" required disabled={rowsLocked}
                                    placeholder={c.changeType === 'evidence-moved' ? 'Reason for the GPS correction — required, stamped in the record' : 'Reason — required, recorded in the retraction'}
                                    error={!(d.reason || '').trim()}
                                    value={d.reason || ''}
                                    onChange={(ev) => setReason(key, ev.target.value)} sx={{ flexGrow: 1 }} />
                                )}
                              </Stack>
                            )}
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </>
              )}

              {informational.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Detected, not auto-applied</Typography>
                  {informational.map((c, i) => (
                    <Box key={i} sx={{ borderLeft: '3px solid', borderColor: 'info.main', pl: 1.5, py: 0.5, my: 0.5 }}>
                      <Typography variant="body2"><ChangeSummary c={c} /></Typography>
                      <Typography variant="caption" color="text.secondary">{c.recommend}</Typography>
                    </Box>
                  ))}
                </>
              )}

              {soft.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Cosmetic (labels/colors)</Typography>
                  {soft.map((c, i) => (
                    <Box key={i} sx={{ pl: 1.5, py: 0.25 }}>
                      <Typography variant="body2" color="text.secondary"><ChangeSummary c={c} /></Typography>
                    </Box>
                  ))}
                </>
              )}

              {!alreadyApplied && hardAppliable.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button variant="contained" disabled={rowsLocked || undecided.length > 0 || missingReason.length > 0 || (review.errors || []).length > 0} onClick={onApply}>
                    Apply decisions
                  </Button>
                  {undecided.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {undecided.length} row{undecided.length > 1 ? 's' : ''} still undecided
                    </Typography>
                  )}
                  {undecided.length === 0 && missingReason.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {missingReason.length} approved retraction{missingReason.length > 1 ? 's need' : ' needs'} a written reason
                    </Typography>
                  )}
                </Box>
              )}

              {applyResult && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Applied {applyResult.applied?.length || 0} · skipped {applyResult.skipped?.length || 0}
                  {applyResult.stale?.length ? ` · stale ${applyResult.stale.length}` : ''}
                  {applyResult.boundaryAdded?.length ? ` · ${applyResult.boundaryAdded.length} polygon${applyResult.boundaryAdded.length > 1 ? 's' : ''} added to the site boundary` : ''}
                  {applyResult.artifactId ? ` · new Library layer created (artifact #${applyResult.artifactId})` : ''}
                  {applyResult.reused ? ' (idempotent re-run — original result returned)' : ''} — recorded in the Change Log.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </>
  );
}
